"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlayerAvatar } from "@/components/player-avatar";
import { ApiError, friendlyApiMessage } from "@/lib/api-client";
import { reportError } from "@/lib/client-log";
import { updatePlayerProfile, type PlayerProfile } from "@/lib/daily-word";
import { useMessages } from "@/lib/i18n/client";
import messages from "./ranking.i18n";

// Cada persona decide cómo aparece en el ranking: sumarse o no, alias (o su
// primer nombre) y si muestra su foto (o el avatar generado).
export function PlayerProfileDialog({
  open,
  onOpenChange,
  profile,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: PlayerProfile;
  onSaved: (profile: PlayerProfile) => void;
}) {
  const t = useMessages(messages);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.profile.title}</DialogTitle>
          <DialogDescription>{t.profile.description}</DialogDescription>
        </DialogHeader>
        {/* key: cada vez que se abre parte desde lo guardado. */}
        {open && <ProfileForm key={String(open)} profile={profile} onCancel={() => onOpenChange(false)} onSaved={onSaved} />}
      </DialogContent>
    </Dialog>
  );
}

function ProfileForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: PlayerProfile;
  onCancel: () => void;
  onSaved: (profile: PlayerProfile) => void;
}) {
  const t = useMessages(messages);
  const [joined, setJoined] = useState(profile.joined || !profile.promptAnswered);
  const [alias, setAlias] = useState(profile.alias ?? "");
  const [showPhoto, setShowPhoto] = useState(profile.showPhoto);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewName = alias.trim() || profile.firstName;
  const photoVisible = !!profile.photoUrl && showPhoto;
  const preview = {
    name: previewName,
    avatarUrl: photoVisible ? profile.photoUrl : null,
    avatarSeed: profile.card.avatarSeed,
  };

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const saved = await updatePlayerProfile({ joined, alias: alias.trim() || null, showPhoto });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 400 || err.status === 409)) {
        setError(friendlyApiMessage(err.message, t.profile.error));
      } else {
        reportError("player-profile-save", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
        setError(t.profile.error);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-5">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={joined}
          onChange={(e) => setJoined(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-primary"
        />
        <span>
          <span className="block text-sm font-medium">{t.profile.joined}</span>
          <span className="block text-xs text-muted-foreground">{t.profile.joinedHelp}</span>
        </span>
      </label>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="player-alias">{t.profile.alias}</Label>
        <Input
          id="player-alias"
          value={alias}
          maxLength={20}
          placeholder={profile.firstName}
          autoComplete="off"
          onChange={(e) => setAlias(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t.profile.aliasHelp(profile.firstName)}</p>
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={photoVisible}
          disabled={!profile.photoUrl}
          onChange={(e) => setShowPhoto(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-primary disabled:opacity-50"
        />
        <span>
          <span className="block text-sm font-medium">{t.profile.showPhoto}</span>
          {!photoVisible && (
            <span className="block text-xs text-muted-foreground">
              {profile.photoUrl ? t.profile.photoHidden : t.profile.noPhoto}
            </span>
          )}
        </span>
      </label>

      <div className="rounded-xl border border-border p-3">
        <p className="text-xs font-medium text-muted-foreground">{t.profile.preview}</p>
        <div className="mt-2 flex items-center gap-3">
          <PlayerAvatar player={preview} size={40} />
          <span className="truncate font-semibold">{previewName}</span>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-10 items-center justify-center rounded-full border border-border px-5 text-sm font-semibold hover:bg-muted"
        >
          {t.profile.cancel}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? t.profile.saving : t.profile.save}
        </button>
      </div>
    </form>
  );
}
